import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { IFormBuilderAssistant } from '../../../components/form-builder/form-builder-assistant/iform-builder-assistant.interface';
import { DrawerComponentLiaisonBase } from '../../drawers/drawer-base-classes';
import { IDrawerComponentLiaison } from '../../../interfaces/idrawer-component-liaison';
import { FormFieldPropertiesComponent } from '../../../components/form-builder/properties/form-field-properties/form-field-properties.component';
import { FormPropertiesComponent } from '../../../components/form-builder/properties/form-properties/form-properties.component';
import { LayoutRowPropertiesComponent } from '../../../components/form-builder/properties/layout-row-properties/layout-row-properties.component';
import { FormConditionalPropertiesComponent } from '../../../components/form-builder/properties/form-conditional-properties/form-conditional-properties.component';
import { FormField } from '../form-field.model';
import { FormFieldExpression } from '../../field-conditional-logic/form-field-expression.model';

export class FormFieldPropertiesDrawerComponentLiaison extends DrawerComponentLiaisonBase implements IDrawerComponentLiaison {
    public constructor(private component: FormBuilderComponent) {
        super();

        this.initializationHasCompleted();
    }

    // Implement IDrawerComponentLiaison methods.
    public get HandlesDrawerComponentTag(): string {
        return 'app-form-field-properties';
    }

    public drawerComponentInitialized(drawerComponentParam: any): void {
        let drawerComponent: FormFieldPropertiesComponent = drawerComponentParam;

        // Set the drawer's input value(s).
        drawerComponent.field = this.component.SelectedField;
        drawerComponent.form = this.component.Form;
        drawerComponent.properties = this.component.SelectedFieldProperties;
        drawerComponent.constraintNames = this.component.SelectedConstraintNames;
        drawerComponent.customRegexConstraintName = this.component.CustomRegexConstraintName;
        drawerComponent.displayFormatValues = this.component.SelectedFieldDisplayFormatValues;
        drawerComponent.displayRowsValues = this.component.SelectedFieldDisplayRowsValues;
        // TO DO:  CONSIDER MOVING SOME OF THE LOGIC FOR THE ABOVE INPUTS OUT OF THE FORM BUILDER COMPONENT CLASS.

        // Listen for the component's outputs.
        drawerComponent.propertyChanged.subscribe(event => {
            this.propertyChanged(event);
        });

        drawerComponent.onClose.subscribe(event => {
            this.closeProperties(event);
        });
    }
    public drawerComponentDestroyed(drawerComponentParam: any): void {
        // NOTE:  THIS METHOD IS A NOOP BY DESIGN.
    }

    // Helper methods.
    private propertyChanged(e: any): void {
        //Logging.all("FormBuilder.propertyChanged()", e);
        let formField: FormField = e.formField;
        let propertyName: string = e.propertyName;

        this.component.notifyFormFieldOfPropertyUpdate(formField, propertyName);
        if (e.cancelEdit) {
            this.component.clearDirtyFlag();
        } else {
            this.component.flagDirty();
        }

        return;
    }

    /*
    * PJH - 2/5/2025
    * I spent a lot of time with this method and encountered some very confusing behavior
    * I was working with the contacts and attachments grids and found that when I went to edit the properties of a column in those grids and then
    * clicked "Cancel" the whole grid would disappear.
    *
    * What was happening was that the Contacts field got replaced with child field being edited
    * It took me ages to find where that was happening.
    * 
    * The confusing thing is that the "reset" logic that needs to happen when canceling a field property edit happens in three different places.
    * 1) Some of it happens below directly in this method
    * 2) Some of it happens in FormFieldProperties.cancelClicked()
    * 3) Some of it happens in FormBuilder.drawerClosing() when this.component.FormFieldProperties.applyingFormFieldProperties is false
    *    That method calls this.formFieldProperties.selectedComponent.resetFormField(this.formFieldProperties.savedUnchangedSelectedField);
    *
    * What I ended up having to do was to override resetFormField() in the Contacts and Attachments component so they could exhibit grid behvaior.
    * The default behavior of that method in FormFieldBase was what was causing the issue
    * 
    * TODO: Figure out if all these resets are needed/streamline this whole deal
    */
    private closeProperties(applyChanges: boolean): void {
        this.component.FormFieldProperties.applyingFormFieldProperties = applyChanges; // Will prevent undo logic.
        if (applyChanges) {
            // Call the component's propertyUpdated() method with pseudo-property
            // name 'allProperties' to indicate that all properties are being applied.
            this.component.FormFieldProperties.selectedComponent.propertyUpdated(this.component.FormFieldProperties.SelectedField, 'allProperties');
        }

        this.component.drawer.close();

        if (applyChanges && (this.component.FormFieldProperties.onEditSaveCallback != null))
            this.component.FormFieldProperties.onEditSaveCallback(this.component.FormFieldProperties.SelectedField);

        this.component.DrawerComponentLiaison = null;
    }
}

export class FormPropertiesDrawerComponentLiaison extends DrawerComponentLiaisonBase implements IDrawerComponentLiaison {
    private alwaysShowCellSelectionIsModified: boolean = false;
    private formPropertiesDrawerComponent: FormPropertiesComponent = null;

    public constructor(private component: FormBuilderComponent, private assistant: IFormBuilderAssistant) {
        super();

        this.initializationHasCompleted();
    }

    // Implement IDrawerComponentLiaison methods.
    public get HandlesDrawerComponentTag(): string {
        return 'app-form-properties';
    }

    public drawerComponentInitialized(drawerComponentParam: any): void {
        let drawerComponent: FormPropertiesComponent = drawerComponentParam;
        this.alwaysShowCellSelectionIsModified = drawerComponent.AlwaysShowCellSelectionIsModified;

        // Set the drawer's input value(s).
        drawerComponent.form = this.component.EditForm;
        //drawerComponent.titleCanBeEdited = this.assistant.titleCanBeConfigured;

        // Listen for the component's outputs.
        drawerComponent.onClose.subscribe(applyChanges => {
            this.closeFormSettings(applyChanges);
        });
        this.formPropertiesDrawerComponent = drawerComponent;
    }
    public drawerComponentDestroyed(drawerComponentParam: any): void {
        // NOTE:  THIS METHOD IS A NOOP BY DESIGN.
    }

    // Helper methods.
    private closeFormSettings(applyChanges: boolean): void {
        if (applyChanges) {
            // Begin 10-26-2020 change:  call the following line, now
            //                           commented out, in order to avoid an
            //                           ExpressionChangedAfterItHasBeenCheckedError
            //                           error that was being thrown whenever a form's
            //                           default number of column was changed.  This
            //                           was happening with zone.js versions 0.10.3,
            //                           0.10.2, and 0.10.1.  I also tried re-enabling
            //                           the import "import "es6-shim";" in src/main.ts,
            //                           but the error kept occurring.  Calling the
            //                           commented out line after a minimal timeout
            //                           overcomes the error and is known to be a
            //                           workaround, albeit an ugly one, for the
            //                           aforementioned error.
            //this.form.formLayout.applyChanges();

            this.component.ApplyingFormSettings = true;

            setTimeout(() => {
                this.component.form.formLayout.applyChanges();
                this.component.updateRowsWithFullWidthFields();

                this.component.EditForm = null;
                this.component.SavedFormCopy = null;
                this.component.ApplyingFormSettings = false;

                this.component.DrawerComponentLiaison = null;
                this.formPropertiesDrawerComponent = null;

            }, 0);
            // End 10-26-2020 change.

            this.component.flagDirty();
        } else {
            // Restore prior settings when cancel is clicked.
            setTimeout(() => {
                this.component.form.formLayout = this.component.SavedFormCopy.formLayout;
                this.component.form.workflowId = this.component.SavedFormCopy.workflowId;

                //***Cancel changes made to always show selection checkbox if it was changed
                if (this.formPropertiesDrawerComponent != null &&
                    this.formPropertiesDrawerComponent.AlwaysShowCellSelectionIsModified)
                    this.component.form.alwaysShowCellSelection = !this.component.form.alwaysShowCellSelection;

                this.component.EditForm = null;
                this.component.SavedFormCopy = null;

                this.component.DrawerComponentLiaison = null;
                this.formPropertiesDrawerComponent = null;
            }, 0);
        }

        this.component.drawer.close();
    }

}

export class FormConditionalPropertiesDrawerComponentLiaison extends DrawerComponentLiaisonBase implements IDrawerComponentLiaison {
    public constructor(private component: FormBuilderComponent, private editFieldConditionalLogic: FormField) {
        super();

        this.initializationHasCompleted();
    }

    // Implement IDrawerComponentLiaison methods.
    public get HandlesDrawerComponentTag(): string {
        return 'app-form-conditional-properties';
    }
    public get EditFieldConditionalLogic(): FormField {
        return this.editFieldConditionalLogic;
    }

    public drawerComponentInitialized(drawerComponentParam: any): void {
        let drawerComponent: FormConditionalPropertiesComponent = drawerComponentParam;

        // Set the drawer's input value(s).
        drawerComponent.form = this.component.Form;
        drawerComponent.form.hideSaveButton = this.component.Form.hideSaveButton;
        drawerComponent.form.confirmSavePopup = this.component.Form.confirmSavePopup;
        drawerComponent.form.enableBulkWorkflow = this.component.Form.enableBulkWorkflow;
        drawerComponent.field = this.editFieldConditionalLogic;
        drawerComponent.existingRules =
            //this.component.ExistingRules;
            FormFieldExpression.getRulesForSourceFormField(
                //this.fieldConditionalLogicData.editFieldConditionalLogic.clientId,
                //this.editFieldConditionalLogic.clientId,
                this.editFieldConditionalLogic.name,
                this.component.form.conditionalLogicRules);

        // Listen for the component's outputs.
        drawerComponent.onClose.subscribe(event => {
            this.closeConditionalLogicSettings(event);
        });
    }
    public drawerComponentDestroyed(drawerComponentParam: any): void {
        // NOTE:  THIS METHOD IS A NOOP BY DESIGN.
    }

    // Helper methods.
    private closeConditionalLogicSettings(rulesData: FormFieldExpression[]): void {
        // If there is event data, save it.
        if (rulesData) {
            // Integrate the newly defined rules with
            // any existing conditional logic rules.
            this.component.form.conditionalLogicRules =
                //FormConditionalLogicRule.addRulesForSourceFormField(this.editFieldConditionalLogic.clientId,
                FormFieldExpression.addRulesForSourceFormField(
                    //this.component.EditFieldConditionalLogic.clientId,
                    //this.editFieldConditionalLogic.clientId,
                    this.editFieldConditionalLogic.name,
                    this.component.form.conditionalLogicRules,
                    rulesData);
            this.component.flagDirty();
        }

        // Close the drawer.
        this.component.drawer.close();

        this.component.DrawerComponentLiaison = null;
    }
}
